import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import {
  Search,
  FilterAltOff,
  ManageAccounts,
  Edit,
  Delete,
  CalendarViewWeek,
  ExpandMore,
  ExpandLess,
  UnfoldMore,
  UnfoldLess,
  ContentCopy,
  DeleteSweep,
  FileDownload,
} from '@mui/icons-material';

import { useClassTable } from '../hooks/useClassTable';
import { useTranslation } from '../i18n';
import { useClassCalendar } from '../hooks/useClassCalendar';
import { useWeeklyClassSheet } from '../hooks/useWeeklyClassSheet';
import ClassCalendarView from './ClassCalendar/ClassCalendarView';
import { apiGet } from '../utils/apiClient';
import { notifyFailure } from '../utils/toast';
import { TableNoData, TableSpacerRow } from './common/TableEmptyState';
import TableLoader from './common/TableLoader';
import {
  MANAGEMENT_TABLE_HEADER_COLOR,
  ManagementTableActionCell,
  ManagementTableHeaderCell,
  managementTableRowSx,
} from './common/ManagementTable';
import { serviceTypeColors } from '../utils/colorHandler';
import ColorChip from './common/ColorChip';
import SofLevelChip from './common/SofLevelChip';
import { EmptyValue } from './common/EmptyValue';
import ClassCardList from './ClassCardList';
import { useIsMobile } from '../hooks/useResponsive';
import { Class, Subtype, ClassStatus } from '../types/ClassTable.type';
import {
  ClassCalendarFilters,
  ClassCalendarItem,
} from '../types/ClassCalendar.type';
import ClassMembersDialog from './FormDialog/ClassMembersDialog';
import CreateClassDialog from './Class/CreateClassDialog';
import UpdateClassDialog from './Class/UpdateClassDialog';
import QuickAddClassDialog from './Class/QuickAddClassDialog';
import CloneClassDialog from './Class/CloneClassDialog';
import {
  useState,
  useMemo,
  useEffect,
  Fragment,
  type Dispatch,
  type SetStateAction,
} from 'react';
const levelColumnSx = {
  width: 140,
  minWidth: 140,
  whiteSpace: 'nowrap' as const,
};

// The "Add Single Class" / "Add Classes" buttons are rendered by the Class page
// (above the metrics), so it can drive the dialogs that live in this table via
// these optional props. When omitted, the table falls back to its own hook
// state (keeps the component usable standalone).
type ClassTableProps = {
  openCreateClassDialog?: boolean;
  setOpenCreateClassDialog?: Dispatch<SetStateAction<boolean>>;
  openQuickAddClassDialog?: boolean;
  setOpenQuickAddClassDialog?: Dispatch<SetStateAction<boolean>>;
  calendarView?: boolean;
  onCalendarViewChange?: (value: boolean) => void;
  // Controlled list view when the parent owns the view tabs.
  viewMode?: 'all' | 'grouped';
};

const ClassTable = (props: ClassTableProps = {}) => {
  const {
    classes,
    selectedClass,
    setSelectedClass,

    page,
    setPage,
    limit,
    totalItems,

    fetchClasses,
    openCreateClassDialog: hookOpenCreateClassDialog,
    setOpenCreateClassDialog: hookSetOpenCreateClassDialog,
    openQuickAddClassDialog: hookOpenQuickAddClassDialog,
    setOpenQuickAddClassDialog: hookSetOpenQuickAddClassDialog,
    openClassInfoDialog,
    setOpenClassInfoDialog,
    handleCloseClassInfoDialog,
    handleDeleteClass,
    handleDeleteWeek,
    deleteWeekLoading,
    openDeleteClassDialog,
    setOpenDeleteClassDialog,
    openClassMembersDialog,
    setOpenClassMembersDialog,

    searchTerm,
    subTypeFilter,
    setSubTypeFilter,
    statusFilter,
    setStatusFilter,
    levelFilter,
    setLevelFilter,
    capacityFilter,
    setCapacityFilter,
    dateRangeFilter,
    setDateRangeFilter,
    dayOfWeekFilter,
    setDayOfWeekFilter,
    timeSlotFilter,
    setTimeSlotFilter,
    coachFilter,
    setCoachFilter,
    coachOptions,
    serviceOptions,

    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    clearFilters,
    handleFilterSubtypeChange,
    handleExportWeekReport,
    exportWeekLoading,
    handleDownloadClasses,
    downloadLoading,

    emptyRows,
    lastSyncTime,
    highlightedRow,
    setHighlightedRow,
    viewMode,
    setViewMode,
    loading,
  } = useClassTable({ enabled: !props.calendarView });
  const { t } = useTranslation();

  // The view tabs live in the parent page now; mirror its choice into the hook
  // so grouping/fetching still follows the selected list view.
  useEffect(() => {
    if (props.viewMode && props.viewMode !== viewMode) {
      setViewMode(props.viewMode);
    }
  }, [props.viewMode, viewMode, setViewMode]);

  // Prefer the parent-controlled dialog state (buttons live on the Class page);
  // otherwise use this table's own hook state.
  const openCreateClassDialog =
    props.openCreateClassDialog ?? hookOpenCreateClassDialog;
  const setOpenCreateClassDialog =
    props.setOpenCreateClassDialog ?? hookSetOpenCreateClassDialog;
  const openQuickAddClassDialog =
    props.openQuickAddClassDialog ?? hookOpenQuickAddClassDialog;
  const setOpenQuickAddClassDialog =
    props.setOpenQuickAddClassDialog ?? hookSetOpenQuickAddClassDialog;

  const isMobile = useIsMobile();

  // Row actions, shared by the desktop table and the mobile card list.
  const handleManageClass = (record: Class) => {
    setSelectedClass(record);
    setOpenClassMembersDialog(true);
  };
  const handleEditClass = (record: Class) => {
    setSelectedClass(record);
    setOpenClassInfoDialog(true);
  };
  const handleRequestDeleteClass = (record: Class) => {
    setSelectedClass(record);
    setOpenDeleteClassDialog(true);
  };

  const calendarFilters = useMemo<ClassCalendarFilters>(() => {
    const exactDate =
      dateRangeFilter.from && dateRangeFilter.from === dateRangeFilter.to
        ? dateRangeFilter.from
        : undefined;

    return {
      search: searchTerm,
      subtype: subTypeFilter.id === -1 ? 'All' : subTypeFilter.name,
      status: statusFilter,
      level: levelFilter,
      capacity: capacityFilter,
      timeSlot: timeSlotFilter,
      coachId: coachFilter === 'All' ? undefined : coachFilter,
      daysOfWeek: dayOfWeekFilter.length
        ? dayOfWeekFilter.join(',')
        : undefined,
      date: exactDate,
      dateFrom: exactDate ? undefined : dateRangeFilter.from || undefined,
      dateTo: exactDate ? undefined : dateRangeFilter.to || undefined,
    };
  }, [
    coachFilter,
    dateRangeFilter.from,
    dateRangeFilter.to,
    dayOfWeekFilter,
    levelFilter,
    capacityFilter,
    searchTerm,
    statusFilter,
    subTypeFilter.id,
    subTypeFilter.name,
    timeSlotFilter,
  ]);

  const classCalendar = useClassCalendar({
    role: 'admin',
    enabled: Boolean(props.calendarView),
    filters: calendarFilters,
  });

  const weeklyClassSheet = useWeeklyClassSheet({
    role: 'admin',
    enabled: Boolean(props.calendarView),
    range: classCalendar.range,
    filters: calendarFilters,
  });

  const handleSheetSubtypeChange = (value: string) => {
    if (value === 'All') {
      setSubTypeFilter({ id: -1, name: 'All' });
      return;
    }

    const selectedSubtype = serviceOptions
      .flatMap((service) => service.subtypes || [])
      .find((subtype) => subtype.name === value);

    if (selectedSubtype) setSubTypeFilter(selectedSubtype);
  };

  const handleCalendarAction = async (
    item: ClassCalendarItem,
    action: 'detail' | 'manage' | 'edit' | 'delete',
  ) => {
    try {
      const response = await apiGet<{ data: Class }>(
        `/class/detail/${item.id}`,
      );
      setSelectedClass(response.data);
      if (action === 'manage') {
        setOpenClassMembersDialog(true);
      } else if (action === 'edit') {
        setOpenClassInfoDialog(true);
      } else if (action === 'delete') {
        setOpenDeleteClassDialog(true);
      } else {
        setOpenClassInfoDialog(true);
      }
    } catch {
      notifyFailure();
    }
  };

  const handleCalendarSelect = (item: ClassCalendarItem) => {
    void handleCalendarAction(item, 'detail');
  };

  const refreshClassData = () => {
    void fetchClasses();
    if (props.calendarView) {
      classCalendar.refresh();
    }
  };

  const confirmDeleteClass = async () => {
    if (!selectedClass) return;

    await handleDeleteClass(selectedClass);
    if (props.calendarView) {
      classCalendar.refresh();
    }
    setOpenDeleteClassDialog(false);
  };

  // Level options from data
  const levelOptions = [
    'All',
    'Starfish',
    'Development',
    'Purple',
    'Seahorse',
    'Seal',
    'Bronze',
    'Barracuda',
    'Silver',
    'Swordfish',
    'Gold',
    'Dolphin',
    'Elite',
  ];

  // Day of week options
  const daysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  // Time slot options
  const timeSlots = [
    { value: 'All', label: t('class.timeAll') },
    { value: 'morning', label: t('class.timeMorning') },
    { value: 'afternoon', label: t('class.timeAfternoon') },
    { value: 'evening', label: t('class.timeEvening') },
  ];

  const coachTeacherCellSx = {
    width: 280,
    maxWidth: 280,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  } as const;

  // Date range quick presets
  const handleDatePreset = (preset: string) => {
    const today = new Date();
    let from = '';
    let to = '';

    switch (preset) {
      case 'today':
        from = to = today.toISOString().split('T')[0];
        break;
      case 'thisWeek': {
        const dayOfWeek = today.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + diff);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        from = monday.toISOString().split('T')[0];
        to = sunday.toISOString().split('T')[0];
        break;
      }
      case 'nextWeek': {
        const dayOfWeek = today.getDay();
        const diff = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + diff);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        from = monday.toISOString().split('T')[0];
        to = sunday.toISOString().split('T')[0];
        break;
      }
      case 'thisMonth': {
        from = new Date(today.getFullYear(), today.getMonth(), 1)
          .toISOString()
          .split('T')[0];
        to = new Date(today.getFullYear(), today.getMonth() + 1, 0)
          .toISOString()
          .split('T')[0];
        break;
      }
    }

    setDateRangeFilter({ from, to });
  };

  const handleDayOfWeekToggle = (day: string) => {
    setDayOfWeekFilter((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      } else {
        return [...prev, day];
      }
    });
  };

  const handleClassDateChange = (date: string) => {
    setDateRangeFilter({ from: date, to: date });
  };

  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [openCloneClassDialog, setOpenCloneClassDialog] = useState(false);
  const [selectedWeekForClone, setSelectedWeekForClone] = useState<{
    startDate: string;
    label: string;
  } | null>(null);
  const [openDeleteWeekDialog, setOpenDeleteWeekDialog] = useState(false);
  const [selectedWeekForDelete, setSelectedWeekForDelete] = useState<{
    startDate: string;
    label: string;
    classCount: number;
  } | null>(null);

  // Helper function to get week range
  const getWeekRange = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
    const monday = new Date(d.setDate(diff));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return {
      start: monday,
      end: sunday,
      key: `${monday.getDate()}/${monday.getMonth() + 1}/${monday.getFullYear()}`,
    };
  };

  // Helper function to format date range
  const formatDateRange = (start: Date, end: Date) => {
    const formatDate = (d: Date) => {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    };
    return `${formatDate(start)} to ${formatDate(end)}`;
  };

  const formatDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleRequestDeleteWeek = (weekGroup: {
    weekRange: { start: Date; end: Date };
    classes: Class[];
  }) => {
    setSelectedWeekForDelete({
      startDate: formatDateKey(weekGroup.weekRange.start),
      label: formatDateRange(
        weekGroup.weekRange.start,
        weekGroup.weekRange.end,
      ),
      classCount: weekGroup.classes.length,
    });
    setOpenDeleteWeekDialog(true);
  };

  const confirmDeleteWeek = async () => {
    if (!selectedWeekForDelete) return;

    const success = await handleDeleteWeek(selectedWeekForDelete.startDate);
    if (success) {
      setOpenDeleteWeekDialog(false);
      setSelectedWeekForDelete(null);
    }
  };

  // Group classes by week
  const groupedClasses = useMemo(() => {
    const groups = new Map<string, typeof classes>();

    classes.forEach((cls) => {
      if (cls.classDate) {
        const weekRange = getWeekRange(new Date(cls.classDate));
        const key = weekRange.key;

        if (!groups.has(key)) {
          groups.set(key, []);
        }
        const group = groups.get(key);
        if (group) {
          group.push(cls);
        }
      }
    });

    // Sort groups by week start date (newest first)
    return Array.from(groups.entries())
      .map(([key, classesInWeek]) => {
        const firstClass = classesInWeek[0];
        const weekRange = getWeekRange(
          new Date(firstClass.classDate || new Date()),
        );
        return {
          key,
          weekRange,
          classes: classesInWeek.sort((a, b) => {
            // Sort by date, then by start time
            const dateCompare =
              new Date(a.classDate || 0).getTime() -
              new Date(b.classDate || 0).getTime();
            if (dateCompare !== 0) return dateCompare;
            return (a.startTime || '').localeCompare(b.startTime || '');
          }),
        };
      })
      .sort(
        (a, b) => b.weekRange.start.getTime() - a.weekRange.start.getTime(),
      );
  }, [classes]);

  const toggleWeek = (weekKey: string) => {
    setExpandedWeeks((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(weekKey)) {
        newSet.delete(weekKey);
      } else {
        newSet.add(weekKey);
      }
      return newSet;
    });
  };

  const expandAllWeeks = () => {
    setExpandedWeeks(new Set(groupedClasses.map((weekGroup) => weekGroup.key)));
  };

  const collapseAllWeeks = () => setExpandedWeeks(new Set());

  return (
    <>
      <Paper>
        <Box
          p={1.5}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <TextField
            size="medium"
            placeholder={t('class.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                fetchClasses();
                setPage(1);
              }
            }}
            sx={{ width: { xs: '100%', sm: 320 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                size: 'small',
              },
            }}
          />
          <Box
            display="flex"
            sx={{ gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                displayEmpty
                value={subTypeFilter.id.toString()}
                onChange={handleFilterSubtypeChange}
              >
                <MenuItem value="-1">{t('class.allServiceTypes')}</MenuItem>
                {serviceOptions.length > 0 &&
                  serviceOptions[0].subtypes.map((option: Subtype) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                displayEmpty
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <MenuItem value="All">{t('class.allStatus')}</MenuItem>
                <MenuItem value="ACTIVE">{t('class.statusActive')}</MenuItem>
                <MenuItem value="CLOSED">{t('class.statusClosed')}</MenuItem>
                <MenuItem value="CANCELLED">
                  {t('class.statusCancelled')}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                displayEmpty
                value={capacityFilter}
                onChange={(event) => {
                  setCapacityFilter(
                    event.target.value as 'All' | 'AVAILABLE' | 'FULL',
                  );
                  setPage(1);
                }}
                aria-label="Filter by capacity"
              >
                <MenuItem value="All">All Capacities</MenuItem>
                <MenuItem value="AVAILABLE">Available</MenuItem>
                <MenuItem value="FULL">Not Available</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                displayEmpty
                value={levelFilter}
                onChange={(event) => setLevelFilter(event.target.value)}
              >
                {levelOptions.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level === 'All' ? t('class.allLevels') : level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                displayEmpty
                value={timeSlotFilter}
                onChange={(event) => setTimeSlotFilter(event.target.value)}
              >
                {timeSlots.map((slot) => (
                  <MenuItem key={slot.value} value={slot.value}>
                    {slot.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 170 }}>
              <Select
                displayEmpty
                value={coachFilter}
                onChange={(event) => setCoachFilter(event.target.value)}
                aria-label="Filter by coach teacher"
              >
                <MenuItem value="All">{t('class.allCoachTeachers')}</MenuItem>
                {coachOptions.map((coach) => (
                  <MenuItem key={coach.id} value={coach.id.toString()}>
                    {coach.name || coach.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="date"
              label={t('class.classDate')}
              value={
                dateRangeFilter.from === dateRangeFilter.to
                  ? dateRangeFilter.from
                  : ''
              }
              onChange={(event) => handleClassDateChange(event.target.value)}
              aria-label="Filter by class date"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 165 }}
            />

            {/* Clear filters button */}
            {(searchTerm ||
              subTypeFilter?.id !== -1 ||
              statusFilter !== 'All' ||
              levelFilter !== 'All' ||
              dateRangeFilter.from ||
              dateRangeFilter.to ||
              dayOfWeekFilter.length > 0 ||
              timeSlotFilter !== 'All' ||
              coachFilter !== 'All' ||
              capacityFilter !== 'All') && (
              <Button
                size="small"
                variant="contained"
                color="info"
                onClick={clearFilters}
                startIcon={<FilterAltOff />}
              >
                {t('class.clear')}
              </Button>
            )}

            <Tooltip title={t('class.downloadList')}>
              <IconButton
                onClick={handleDownloadClasses}
                loading={downloadLoading}
                disabled={totalItems === 0 || downloadLoading}
              >
                <FileDownload />
              </IconButton>
            </Tooltip>

            {viewMode === 'grouped' && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UnfoldMore />}
                  onClick={expandAllWeeks}
                  disabled={groupedClasses.length === 0}
                >
                  {t('class.expandAll')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UnfoldLess />}
                  onClick={collapseAllWeeks}
                  disabled={expandedWeeks.size === 0}
                >
                  {t('class.collapseAll')}
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Advanced Filters Toggle */}
        <Box
          px={2}
          py={1.5}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'action.hover',
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'action.hover' },
            transition: 'all 0.2s ease',
          }}
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <CalendarViewWeek sx={{ color: 'text.primary', fontSize: 22 }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="text.primary"
            >
              {t('class.advancedFilters')}
            </Typography>
            {(dateRangeFilter.from ||
              dateRangeFilter.to ||
              dayOfWeekFilter.length > 0) && (
              <Chip
                label={`${(dateRangeFilter.from || dateRangeFilter.to ? 1 : 0) + (dayOfWeekFilter.length > 0 ? 1 : 0)} active`}
                size="small"
                sx={{
                  backgroundColor: '#102138',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: '20px',
                }}
              />
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: 'rgba(16, 33, 56, 0.1)',
              borderRadius: '50%',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.3s ease',
              transform: showAdvancedFilters
                ? 'rotate(180deg)'
                : 'rotate(0deg)',
            }}
          >
            <ExpandMore sx={{ color: 'text.primary' }} />
          </Box>
        </Box>

        {/* Advanced Filters Section */}
        {showAdvancedFilters && (
          <Box
            px={2}
            py={2.5}
            display="flex"
            flexDirection="column"
            gap={3}
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: 'action.hover',
              animation: 'slideDown 0.3s ease-out',
              '@keyframes slideDown': {
                from: {
                  opacity: 0,
                  maxHeight: 0,
                },
                to: {
                  opacity: 1,
                  maxHeight: '500px',
                },
              },
            }}
          >
            {/* Date Range Filter */}
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Typography
                  variant="subtitle2"
                  fontWeight="700"
                  color="text.primary"
                >
                  Date Range
                </Typography>
              </Box>
              <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={dateRangeFilter.from}
                  onChange={(e) =>
                    setDateRangeFilter((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  sx={{
                    width: 170,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                      borderRadius: '8px',
                      '&:hover fieldset': {
                        borderColor: '#42a5f5',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                        borderWidth: '2px',
                      },
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  fontWeight="500"
                >
                  to
                </Typography>
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={dateRangeFilter.to}
                  onChange={(e) =>
                    setDateRangeFilter((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                  sx={{
                    width: 170,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                      borderRadius: '8px',
                      '&:hover fieldset': {
                        borderColor: '#42a5f5',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                        borderWidth: '2px',
                      },
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <Box
                  sx={{
                    height: 30,
                    width: '1px',
                    backgroundColor: '#ccc',
                    mx: 0.5,
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleDatePreset('today')}
                >
                  Today
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleDatePreset('thisWeek')}
                >
                  This Week
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleDatePreset('nextWeek')}
                >
                  Next Week
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleDatePreset('thisMonth')}
                >
                  This Month
                </Button>
              </Box>
            </Box>

            {/* Day of Week Filter */}
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Typography
                  variant="subtitle2"
                  fontWeight="700"
                  color="text.primary"
                >
                  Days of Week
                </Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                {daysOfWeek.map((day) => {
                  const isSelected = dayOfWeekFilter.includes(day);
                  return (
                    <Chip
                      key={day}
                      label={day}
                      onClick={() => handleDayOfWeekToggle(day)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}

        {props.calendarView ? (
          <ClassCalendarView
            view={classCalendar.view}
            anchorDate={classCalendar.anchorDate}
            range={classCalendar.range}
            items={classCalendar.items}
            metadata={classCalendar.metadata}
            loading={classCalendar.loading}
            error={classCalendar.error}
            onViewChange={classCalendar.setView}
            onToday={classCalendar.goToToday}
            onDateChange={classCalendar.goToDate}
            onPrevious={classCalendar.goToPrevious}
            onNext={classCalendar.goToNext}
            onRetry={classCalendar.refresh}
            onSelectItem={handleCalendarSelect}
            onExportWeek={() => {
              void handleExportWeekReport(
                classCalendar.range.from,
                calendarFilters,
              );
            }}
            exportLoading={exportWeekLoading}
            sheetDays={weeklyClassSheet.days}
            sheetMetadata={weeklyClassSheet.metadata}
            sheetLoading={weeklyClassSheet.loading}
            sheetError={weeklyClassSheet.error}
            onSheetRetry={weeklyClassSheet.refresh}
            sheetSubtype={subTypeFilter.id === -1 ? 'All' : subTypeFilter.name}
            onSheetSubtypeChange={handleSheetSubtypeChange}
            itemActions={{
              onManage: (item) => {
                void handleCalendarAction(item, 'manage');
              },
              onEdit: (item) => {
                void handleCalendarAction(item, 'edit');
              },
              onDelete: (item) => {
                void handleCalendarAction(item, 'delete');
              },
            }}
            onBackToList={() => props.onCalendarViewChange?.(false)}
          />
        ) : (
          <>
            {isMobile ? (
              <Box sx={{ p: 1.5 }}>
                {viewMode === 'all' ? (
                  <ClassCardList
                    classes={classes}
                    onManage={handleManageClass}
                    onEdit={handleEditClass}
                    onDelete={handleRequestDeleteClass}
                  />
                ) : (
                  groupedClasses.map((weekGroup) => (
                    <Box key={weekGroup.key} sx={{ mb: 2 }}>
                      <Box
                        onClick={() => toggleWeek(weekGroup.key)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR,
                          color: '#fff',
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          cursor: 'pointer',
                          mb: 1,
                        }}
                      >
                        {expandedWeeks.has(weekGroup.key) ? (
                          <ExpandLess sx={{ color: '#fff' }} />
                        ) : (
                          <ExpandMore sx={{ color: '#fff' }} />
                        )}
                        <CalendarViewWeek
                          sx={{ color: '#fff', fontSize: 20 }}
                        />
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          sx={{ flex: 1 }}
                        >
                          Week{' '}
                          {formatDateRange(
                            weekGroup.weekRange.start,
                            weekGroup.weekRange.end,
                          )}
                        </Typography>
                        <Chip
                          label={`${weekGroup.classes.length}`}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(255,255,255,0.25)',
                            color: '#fff',
                          }}
                        />
                        <Tooltip title="Delete all classes in week">
                          <IconButton
                            size="small"
                            aria-label="delete all classes in week"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleRequestDeleteWeek(weekGroup);
                            }}
                            sx={{
                              color: '#fff',
                              '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.14)',
                              },
                            }}
                          >
                            <DeleteSweep fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {expandedWeeks.has(weekGroup.key) && (
                        <ClassCardList
                          classes={weekGroup.classes}
                          onManage={handleManageClass}
                          onEdit={handleEditClass}
                          onDelete={handleRequestDeleteClass}
                        />
                      )}
                    </Box>
                  ))
                )}
              </Box>
            ) : (
              <TableContainer>
                <Table size="small" padding="checkbox">
                  {/* ********** */}
                  {/* HEADER */}
                  {/* ********** */}
                  <TableHead>
                    <TableRow
                      sx={{ backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR }}
                    >
                      <ManagementTableHeaderCell>
                        {t('class.colId')}
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        {t('class.colClassName')}
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        {t('class.colDate')}
                      </ManagementTableHeaderCell>
                      {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    Start Date
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    End Date
                  </Typography>
                </TableCell> */}
                      <ManagementTableHeaderCell>
                        {t('class.colTime')}
                      </ManagementTableHeaderCell>
                      {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    Number of Days
                  </Typography>
                </TableCell> */}
                      <ManagementTableHeaderCell>
                        {t('class.colClassType')}
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        {t('class.colLevel')}
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        {t('class.colCoachTeacher')}
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        {t('class.colStatus')}
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        {t('class.fieldNote')}
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell sticky>
                        {t('class.colActions')}
                      </ManagementTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  {/* ********** */}
                  {/* DATA MANIPULATION */}
                  {/* ********** */}
                  <TableBody>
                    {loading ? (
                      <TableLoader colSpan={10} />
                    ) : (
                      <>
                        {viewMode === 'all'
                          ? // Regular "Show All" View
                            classes.map((record) => (
                              <TableRow
                                key={record.id}
                                sx={managementTableRowSx(
                                  highlightedRow === `data_${record.id}`,
                                )}
                                onClick={() => {
                                  if (highlightedRow === `data_${record.id}`) {
                                    setHighlightedRow(null);
                                  } else {
                                    setHighlightedRow(`data_${record.id}`);
                                  }
                                }}
                              >
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {record.id}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  <div>{`${record.name}`}</div>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  <div>
                                    {record.classDate
                                      ? new Date(
                                          record.classDate,
                                        ).toLocaleDateString('en-GB')
                                      : '---'}
                                  </div>
                                </TableCell>
                                {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>{new Date(record.startDate).toLocaleDateString()}</div>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>{new Date(record.endDate).toLocaleDateString()}</div>
                  </TableCell> */}
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  <div>
                                    {record.startTime || '---'} -{' '}
                                    {record.endTime || '---'}
                                  </div>
                                </TableCell>
                                {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>{record.duration}</div>
                  </TableCell> */}

                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  <ColorChip
                                    label={record.subtypeName}
                                    palette={serviceTypeColors}
                                  />
                                </TableCell>
                                <TableCell sx={levelColumnSx}>
                                  <SofLevelChip
                                    level={record.level}
                                    serviceType={record.subtypeName}
                                  />
                                </TableCell>
                                <TableCell sx={coachTeacherCellSx}>
                                  <Box sx={coachTeacherCellSx}>
                                    {record.coachTeachers?.join(', ') || (
                                      <EmptyValue />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  <div
                                    style={{
                                      color:
                                        record.status === ClassStatus.ACTIVE
                                          ? '#4caf50'
                                          : record.status === ClassStatus.CLOSED
                                            ? '#f44336'
                                            : '#ff9800',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {record.status === ClassStatus.ACTIVE
                                      ? t('class.statusActive')
                                      : record.status === ClassStatus.CLOSED
                                        ? t('class.statusClosed')
                                        : t('class.statusCancelled')}
                                  </div>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  <div>{record.note || <EmptyValue />}</div>
                                </TableCell>

                                <ManagementTableActionCell
                                  selected={
                                    highlightedRow === `data_${record.id}`
                                  }
                                >
                                  <Tooltip
                                    title={t('class.tooltipManageClass')}
                                  >
                                    <IconButton
                                      color="primary"
                                      aria-label="manage class"
                                      onClick={() => {
                                        setSelectedClass(record);
                                        setOpenClassMembersDialog(true);
                                      }}
                                    >
                                      <ManageAccounts />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('class.tooltipEditClass')}>
                                    <IconButton
                                      color="primary"
                                      aria-label="edit class"
                                      onClick={() => {
                                        setSelectedClass(record);
                                        setOpenClassInfoDialog(true);
                                      }}
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip
                                    title={t('class.tooltipDeleteClass')}
                                  >
                                    <IconButton
                                      color="error"
                                      aria-label="delete class"
                                      onClick={() => {
                                        setSelectedClass(record);
                                        setOpenDeleteClassDialog(true);
                                      }}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Tooltip>
                                </ManagementTableActionCell>
                              </TableRow>
                            ))
                          : // Grouped by Week View
                            groupedClasses.map((weekGroup) => (
                              <Fragment key={weekGroup.key}>
                                {/* Week Header Row */}
                                <TableRow
                                  sx={{
                                    backgroundColor:
                                      MANAGEMENT_TABLE_HEADER_COLOR,
                                    '&:hover': { backgroundColor: '#4298c7' },
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  <TableCell colSpan={10} sx={{ py: 2 }}>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={2}
                                    >
                                      <Box
                                        sx={{
                                          backgroundColor:
                                            'rgba(255, 255, 255, 0.2)',
                                          borderRadius: '50%',
                                          width: 32,
                                          height: 32,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: 'pointer',
                                        }}
                                        onClick={() =>
                                          toggleWeek(weekGroup.key)
                                        }
                                      >
                                        {expandedWeeks.has(weekGroup.key) ? (
                                          <ExpandLess sx={{ color: '#fff' }} />
                                        ) : (
                                          <ExpandMore sx={{ color: '#fff' }} />
                                        )}
                                      </Box>
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1.5}
                                        flex={1}
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() =>
                                          toggleWeek(weekGroup.key)
                                        }
                                      >
                                        <CalendarViewWeek
                                          sx={{ color: '#fff', fontSize: 20 }}
                                        />
                                        <Typography
                                          variant="subtitle1"
                                          fontWeight="600"
                                          sx={{
                                            color: '#fff',
                                            letterSpacing: '0.5px',
                                          }}
                                        >
                                          Week{' '}
                                          {formatDateRange(
                                            weekGroup.weekRange.start,
                                            weekGroup.weekRange.end,
                                          )}
                                        </Typography>
                                        <Chip
                                          label={`${weekGroup.classes.length} ${weekGroup.classes.length === 1 ? 'class' : 'classes'}`}
                                          size="small"
                                          sx={{
                                            backgroundColor:
                                              'rgba(255, 255, 255, 0.9)',
                                            color: '#4da7db',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                          }}
                                        />
                                      </Box>
                                      <Box display="flex" gap={1}>
                                        <Tooltip
                                          title={t('class.tooltipExportWeek')}
                                        >
                                          <IconButton
                                            size="small"
                                            color="inherit"
                                            aria-label="export week report"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const year =
                                                weekGroup.weekRange.start.getFullYear();
                                              const month = String(
                                                weekGroup.weekRange.start.getMonth() +
                                                  1,
                                              ).padStart(2, '0');
                                              const day = String(
                                                weekGroup.weekRange.start.getDate(),
                                              ).padStart(2, '0');
                                              const startDate = `${year}-${month}-${day}`;

                                              handleExportWeekReport(
                                                startDate,
                                                calendarFilters,
                                              );
                                            }}
                                            sx={{
                                              color: '#fff',
                                              '&:hover': {
                                                backgroundColor:
                                                  'rgba(255, 255, 255, 0.14)',
                                              },
                                            }}
                                          >
                                            <FileDownload fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                          title={t('class.tooltipCloneWeek')}
                                        >
                                          <IconButton
                                            size="small"
                                            color="inherit"
                                            aria-label="clone week classes"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const year =
                                                weekGroup.weekRange.start.getFullYear();
                                              const month = String(
                                                weekGroup.weekRange.start.getMonth() +
                                                  1,
                                              ).padStart(2, '0');
                                              const day = String(
                                                weekGroup.weekRange.start.getDate(),
                                              ).padStart(2, '0');
                                              const startDate = `${year}-${month}-${day}`;

                                              setSelectedWeekForClone({
                                                startDate,
                                                label: formatDateRange(
                                                  weekGroup.weekRange.start,
                                                  weekGroup.weekRange.end,
                                                ),
                                              });
                                              setOpenCloneClassDialog(true);
                                            }}
                                            sx={{
                                              color: '#fff',
                                              '&:hover': {
                                                backgroundColor:
                                                  'rgba(255, 255, 255, 0.14)',
                                              },
                                            }}
                                          >
                                            <ContentCopy fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete all classes in week">
                                          <IconButton
                                            size="small"
                                            color="inherit"
                                            aria-label="delete all classes in week"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRequestDeleteWeek(
                                                weekGroup,
                                              );
                                            }}
                                            sx={{
                                              color: '#fff',
                                              '&:hover': {
                                                backgroundColor:
                                                  'rgba(255, 255, 255, 0.14)',
                                              },
                                            }}
                                          >
                                            <DeleteSweep fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                </TableRow>

                                {/* Week's Classes (Collapsible) */}
                                {expandedWeeks.has(weekGroup.key) &&
                                  weekGroup.classes.map((record) => (
                                    <TableRow
                                      key={record.id}
                                      sx={managementTableRowSx(
                                        highlightedRow === `data_${record.id}`,
                                      )}
                                      onClick={() => {
                                        if (
                                          highlightedRow === `data_${record.id}`
                                        ) {
                                          setHighlightedRow(null);
                                        } else {
                                          setHighlightedRow(
                                            `data_${record.id}`,
                                          );
                                        }
                                      }}
                                    >
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {record.id}
                                      </TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <div>{`${record.name}`}</div>
                                      </TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <div>
                                          {record.classDate
                                            ? new Date(
                                                record.classDate,
                                              ).toLocaleDateString('en-GB')
                                            : '---'}
                                        </div>
                                      </TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <div>
                                          {record.startTime || '---'} -{' '}
                                          {record.endTime || '---'}
                                        </div>
                                      </TableCell>

                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <ColorChip
                                          label={record.subtypeName}
                                          palette={serviceTypeColors}
                                        />
                                      </TableCell>
                                      <TableCell sx={levelColumnSx}>
                                        <SofLevelChip
                                          level={record.level}
                                          serviceType={record.subtypeName}
                                        />
                                      </TableCell>
                                      <TableCell sx={coachTeacherCellSx}>
                                        <Box sx={coachTeacherCellSx}>
                                          {record.coachTeachers?.join(', ') || (
                                            <EmptyValue />
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <div
                                          style={{
                                            color:
                                              record.status ===
                                              ClassStatus.ACTIVE
                                                ? '#4caf50'
                                                : record.status ===
                                                    ClassStatus.CLOSED
                                                  ? '#f44336'
                                                  : '#ff9800',
                                            fontWeight: 'bold',
                                          }}
                                        >
                                          {record.status === ClassStatus.ACTIVE
                                            ? 'Active'
                                            : record.status ===
                                                ClassStatus.CLOSED
                                              ? 'Closed'
                                              : 'Cancelled'}
                                        </div>
                                      </TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <div>
                                          {record.note || <EmptyValue />}
                                        </div>
                                      </TableCell>

                                      <ManagementTableActionCell
                                        selected={
                                          highlightedRow === `data_${record.id}`
                                        }
                                      >
                                        <Tooltip
                                          title={t('class.tooltipManageClass')}
                                        >
                                          <IconButton
                                            color="primary"
                                            aria-label="manage class"
                                            onClick={() => {
                                              setSelectedClass(record);
                                              setOpenClassMembersDialog(true);
                                            }}
                                          >
                                            <ManageAccounts />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                          title={t('class.tooltipEditClass')}
                                        >
                                          <IconButton
                                            color="primary"
                                            aria-label="edit class"
                                            onClick={() => {
                                              setSelectedClass(record);
                                              setOpenClassInfoDialog(true);
                                            }}
                                          >
                                            <Edit />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                          title={t('class.tooltipDeleteClass')}
                                        >
                                          <IconButton
                                            color="error"
                                            aria-label="delete class"
                                            onClick={() => {
                                              setSelectedClass(record);
                                              setOpenDeleteClassDialog(true);
                                            }}
                                          >
                                            <Delete />
                                          </IconButton>
                                        </Tooltip>
                                      </ManagementTableActionCell>
                                    </TableRow>
                                  ))}
                              </Fragment>
                            ))}

                        {viewMode === 'all' &&
                          (classes.length === 0 ? (
                            <TableNoData />
                          ) : (
                            <TableSpacerRow rows={emptyRows} />
                          ))}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Other data information */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              pl={2}
            >
              <Typography variant="body2" color="textSecondary">
                {t('class.lastSync')}:{' '}
                {lastSyncTime?.format('DD/MM/YYYY - HH:mm:ss') || 'Never'}
              </Typography>
              <TablePagination
                component="div"
                count={totalItems}
                page={page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={limit}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
              />
            </Box>
          </>
        )}

        {/* Quick Add Class Dialog */}
        {openQuickAddClassDialog && (
          <QuickAddClassDialog
            open={openQuickAddClassDialog}
            handleClose={() => {
              setOpenQuickAddClassDialog(false);
            }}
            fetchClasses={refreshClassData}
            subtypes={
              serviceOptions.length > 0 ? serviceOptions[0].subtypes : []
            }
          />
        )}

        {/* Create Class Dialog */}
        {openCreateClassDialog && (
          // <ClassDialog
          //   open={openCreateClassDialog}
          //   handleClose={() => {
          //     setOpenCreateClassDialog(false);
          //   }}
          //   record={null}
          //   subtypeId={0}
          //   fetchClasses={fetchClasses}
          //   subtypes={
          //     serviceOptions.length > 0 ? serviceOptions[0].subtypes : []
          //   }
          // />
          <CreateClassDialog
            open={openCreateClassDialog}
            handleClose={() => {
              setOpenCreateClassDialog(false);
            }}
            fetchClasses={refreshClassData}
            subtypes={
              serviceOptions.length > 0 ? serviceOptions[0].subtypes : []
            }
          />
        )}

        {/* Update Class Info Dialog */}
        {openClassInfoDialog && selectedClass && (
          <UpdateClassDialog
            open={openClassInfoDialog}
            handleClose={handleCloseClassInfoDialog}
            record={selectedClass}
            subtypeId={selectedClass?.subtype?.id || 0}
            fetchClasses={refreshClassData}
            subtypes={
              serviceOptions.length > 0 ? serviceOptions[0].subtypes : []
            }
          />
        )}

        {/* Class Members Dialog */}
        {openClassMembersDialog && selectedClass && (
          <ClassMembersDialog
            open={openClassMembersDialog}
            handleClose={() => {
              setOpenClassMembersDialog(false);
            }}
            record={selectedClass}
          />
        )}

        {/* Confirm Delete */}
        <Dialog
          fullWidth
          open={openDeleteClassDialog}
          onClose={() => setOpenDeleteClassDialog(false)}
        >
          <DialogTitle>{t('class.deleteTitle')}</DialogTitle>
          <DialogContent>{t('class.deleteConfirm')}</DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenDeleteClassDialog(false);
              }}
            >
              {t('class.cancel')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => void confirmDeleteClass()}
            >
              {t('class.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirm Delete Week */}
        <Dialog
          fullWidth
          open={openDeleteWeekDialog}
          onClose={() => {
            if (!deleteWeekLoading) {
              setOpenDeleteWeekDialog(false);
              setSelectedWeekForDelete(null);
            }
          }}
        >
          <DialogTitle>Delete all classes in this week?</DialogTitle>
          <DialogContent>
            <Typography>
              This will soft-delete{' '}
              <strong>{selectedWeekForDelete?.classCount || 0}</strong>{' '}
              class(es) in{' '}
              <strong>
                {selectedWeekForDelete?.label || 'the selected week'}
              </strong>
              .
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Student registrations, attendance records, coaches, and other
              related data will not be deleted.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={deleteWeekLoading}
              onClick={() => {
                setOpenDeleteWeekDialog(false);
                setSelectedWeekForDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={deleteWeekLoading || !selectedWeekForDelete}
              onClick={() => void confirmDeleteWeek()}
            >
              {deleteWeekLoading ? 'Deleting...' : 'Delete all classes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Clone Class Dialog */}
        {openCloneClassDialog && selectedWeekForClone && (
          <CloneClassDialog
            open={openCloneClassDialog}
            handleClose={() => {
              setOpenCloneClassDialog(false);
              setSelectedWeekForClone(null);
            }}
            onSuccess={() => {
              fetchClasses();
              setOpenCloneClassDialog(false);
              setSelectedWeekForClone(null);
            }}
            sourceWeekStart={selectedWeekForClone.startDate}
            sourceWeekLabel={`Week ${selectedWeekForClone.label}`}
          />
        )}
      </Paper>
    </>
  );
};

export default ClassTable;
